/**
 * DepartmentContentRenderers
 *
 * Extracted department-specific table body content for SeasonalityModeView.
 * Each component renders the specific rows for its department type.
 */

import React from 'react';
import {
  MonthlyTotals,
  MONTH_NAMES,
  getMonthValue,
  createEmptyMonthlyTotals,
} from '../../types';
import { DepartmentTemplate } from '../../config/docTemplate';
import { ROW_COLORS, ExpensesByAccount } from './seasonalityModeTypes';
import { getVarianceColor } from './seasonalityModeHelpers';
import { ProjectionComparisonChart } from './ProjectionComparisonChart';

/**
 * Common props needed by all department content components
 */
export interface DepartmentContentCommonProps {
  dept: DepartmentTemplate;
  comparisonMode: boolean;
  visibleActualMonths: Set<number>;
  formatCurrency: (value: number) => string;
  isConsolidated: boolean;
  targetYear: number;
}

/**
 * Renders the Total Units row for vehicle departments
 */
export const VehicleTotalUnitsRow: React.FC<{
  totalUnits: number;
  monthlyTotalUnits: MonthlyTotals;
  actualDeptMonthly: MonthlyTotals | null | undefined;
  actualDeptTotal: number;
  totalColorClass: string;
  comparisonMode: boolean;
  visibleActualMonths: Set<number>;
  /** Rendered after the label (e.g. the Executive adjust button / adjusted badge) */
  labelAdornment?: React.ReactNode;
}> = ({
  totalUnits,
  monthlyTotalUnits,
  actualDeptMonthly,
  actualDeptTotal,
  totalColorClass,
  comparisonMode,
  visibleActualMonths,
  labelAdornment,
}) => {
  const hasActualData = comparisonMode && actualDeptMonthly !== undefined && actualDeptMonthly !== null;

  return (
    <tr className={totalColorClass}>
      <td className={`min-w-[180px] px-3 py-2 text-sm font-bold sticky left-0 ${totalColorClass}`}>
        {labelAdornment ? <span className="flex items-center gap-1">Total Units{labelAdornment}</span> : 'Total Units'}
      </td>
      {MONTH_NAMES.map((_, i) => {
        const monthNum = i + 1;
        const showActual = comparisonMode && visibleActualMonths.has(monthNum);
        const projValue = getMonthValue(monthlyTotalUnits, monthNum);
        const actualValue = hasActualData ? getMonthValue(actualDeptMonthly!, monthNum) : 0;
        const varianceColor = hasActualData ? getVarianceColor(actualValue, projValue, false) : '';

        return (
          <React.Fragment key={i}>
            <td className="min-w-[70px] px-1 py-2 text-right text-xs font-semibold">
              {projValue.toLocaleString()}
            </td>
            {showActual && (
              <td className={`min-w-[70px] px-1 py-2 text-right text-xs font-semibold bg-blue-50/50 ${varianceColor}`}>
                {hasActualData && actualValue !== 0 ? actualValue.toLocaleString() : <span className="text-gray-300">--</span>}
              </td>
            )}
          </React.Fragment>
        );
      })}
      <td className={`min-w-[100px] px-2 py-2 text-right text-sm font-bold ${totalColorClass}`}>
        {totalUnits.toLocaleString()}
      </td>
      {comparisonMode && visibleActualMonths.size > 0 && (
        <td className={`min-w-[100px] px-2 py-2 text-right text-sm font-bold bg-blue-50/50 ${hasActualData ? getVarianceColor(actualDeptTotal, totalUnits, false) : ''}`}>
          {hasActualData && actualDeptTotal !== 0 ? actualDeptTotal.toLocaleString() : <span className="text-gray-300">--</span>}
        </td>
      )}
    </tr>
  );
};

/**
 * Props for OTHERS department income accounts section
 */
interface OthersIncomeContentProps extends DepartmentContentCommonProps {
  otherIncomeCategory: { accounts: Array<{ code: string; description: string }> } | undefined;
  isIncomeExpanded: boolean;
  toggleCategory: (key: string) => void;
  monthlyGross: MonthlyTotals;
  projectedGross: number;
  // For per-account rendering
  accountsWithData: Array<{ code: string; description: string }>;
  totalProjectedUnits: number;
  getProjectionValue: (dept: string, metric: string) => number | null;
  applySeasonality: (value: number, weights: number[]) => MonthlyTotals;
  getAccountSeasonalityWeights: (code: string) => number[];
  deptWeights: number[];
  expensesByAccount: ExpensesByAccount | undefined;
  // Actual data for comparison mode
  actualGross?: { monthly: MonthlyTotals; ytd: number } | null;
  getActualAccountValue?: (accountCode: string) => { monthly: MonthlyTotals; ytd: number } | null;
}

/**
 * Renders the OTHERS department income accounts section
 */
export const OthersIncomeContent: React.FC<OthersIncomeContentProps> = ({
  dept,
  comparisonMode,
  visibleActualMonths,
  formatCurrency,
  isConsolidated,
  otherIncomeCategory,
  isIncomeExpanded,
  toggleCategory,
  monthlyGross,
  projectedGross,
  accountsWithData,
  totalProjectedUnits,
  getProjectionValue,
  applySeasonality,
  getAccountSeasonalityWeights,
  deptWeights,
  expensesByAccount,
  actualGross,
  getActualAccountValue,
}) => {
  if (!otherIncomeCategory) return null;

  const categoryKey = `${dept.name}-OTHER_INCOME`;
  const hasActualData = comparisonMode && actualGross !== undefined && actualGross !== null;

  return (
    <>
      <tr
        className={`${ROW_COLORS.totalGross} border-b border-gray-200 ${accountsWithData.length > 0 ? 'cursor-pointer hover:opacity-90' : ''}`}
        onClick={() => accountsWithData.length > 0 && toggleCategory(categoryKey)}
      >
        <td className={`min-w-[180px] px-3 py-2 text-sm font-bold sticky left-0 ${ROW_COLORS.totalGross}`}>
          <div className="flex items-center">
            {accountsWithData.length > 0 && (
              <span className="mr-2 text-gray-500 w-4 flex-shrink-0">
                {isIncomeExpanded ? '▼' : '▶'}
              </span>
            )}
            <span className="text-green-700">Others Total</span>
            {accountsWithData.length > 0 && (
              <span className="ml-2 text-xs text-gray-500">({accountsWithData.length})</span>
            )}
          </div>
        </td>
        {MONTH_NAMES.map((_, i) => {
          const monthNum = i + 1;
          const showActual = comparisonMode && visibleActualMonths.has(monthNum);
          const projValue = getMonthValue(monthlyGross, monthNum);
          const actualValue = hasActualData ? getMonthValue(actualGross!.monthly, monthNum) : 0;
          const varianceColor = hasActualData ? getVarianceColor(actualValue, projValue, false) : '';

          return (
            <React.Fragment key={i}>
              <td className="min-w-[75px] px-1 py-2 text-right text-xs font-bold">
                {formatCurrency(projValue)}
              </td>
              {showActual && (
                <td className={`min-w-[70px] px-1 py-2 text-right text-xs font-semibold bg-blue-50/50 ${varianceColor}`}>
                  {hasActualData && actualValue !== 0 ? formatCurrency(actualValue) : <span className="text-gray-300">--</span>}
                </td>
              )}
            </React.Fragment>
          );
        })}
        <td className={`min-w-[100px] px-2 py-2 text-right text-sm font-bold ${ROW_COLORS.totalGross}`}>
          {formatCurrency(projectedGross)}
        </td>
        {comparisonMode && visibleActualMonths.size > 0 && (
          <td className={`min-w-[100px] px-2 py-2 text-right text-sm font-bold bg-blue-50/50 ${hasActualData ? getVarianceColor(actualGross!.ytd, projectedGross, false) : ''}`}>
            {hasActualData && actualGross!.ytd !== 0 ? formatCurrency(actualGross!.ytd) : <span className="text-gray-300">--</span>}
          </td>
        )}
      </tr>

      {isIncomeExpanded && accountsWithData.map(acc => {
        const isDocFee = acc.code === '850';
        let accValue = 0;
        let perUnitLabel = '';
        let monthlyAcc: MonthlyTotals;

        if (isConsolidated) {
          // For consolidated view, use stored values from expensesByAccount
          // All OTHERS accounts (including 850 DOC fee) are income accounts
          const accountKey = `${dept.name}#INCOME_${acc.code}`;
          const preCalculated = expensesByAccount?.[accountKey];
          if (preCalculated) {
            accValue = preCalculated.annualTotal;
            monthlyAcc = {
              month1: preCalculated.monthlyDistribution.month1 || 0,
              month2: preCalculated.monthlyDistribution.month2 || 0,
              month3: preCalculated.monthlyDistribution.month3 || 0,
              month4: preCalculated.monthlyDistribution.month4 || 0,
              month5: preCalculated.monthlyDistribution.month5 || 0,
              month6: preCalculated.monthlyDistribution.month6 || 0,
              month7: preCalculated.monthlyDistribution.month7 || 0,
              month8: preCalculated.monthlyDistribution.month8 || 0,
              month9: preCalculated.monthlyDistribution.month9 || 0,
              month10: preCalculated.monthlyDistribution.month10 || 0,
              month11: preCalculated.monthlyDistribution.month11 || 0,
              month12: preCalculated.monthlyDistribution.month12 || 0,
            };
          } else {
            monthlyAcc = createEmptyMonthlyTotals();
          }
        } else {
          // For regular locations, calculate based on per-unit or direct input
          if (isDocFee) {
            const docFeePerUnit = getProjectionValue(dept.name, 'INCOME_850_PER_UNIT') || 0;
            accValue = docFeePerUnit * totalProjectedUnits;
            perUnitLabel = ` (${formatCurrency(docFeePerUnit)}/u × ${totalProjectedUnits} units)`;
          } else {
            accValue = getProjectionValue(dept.name, `INCOME_${acc.code}`) || 0;
          }
          const accWeights = getAccountSeasonalityWeights(acc.code);
          monthlyAcc = applySeasonality(accValue, accWeights.every(w => w === 1/12) ? deptWeights : accWeights);
        }

        const rawActualAcc = getActualAccountValue ? getActualAccountValue(acc.code) : null;
        // OTHERS accounts are income (credit-side) stored as negative in accounting — show as positive
        const actualAcc = rawActualAcc ? {
          monthly: {
            month1: Math.abs(rawActualAcc.monthly.month1 || 0),
            month2: Math.abs(rawActualAcc.monthly.month2 || 0),
            month3: Math.abs(rawActualAcc.monthly.month3 || 0),
            month4: Math.abs(rawActualAcc.monthly.month4 || 0),
            month5: Math.abs(rawActualAcc.monthly.month5 || 0),
            month6: Math.abs(rawActualAcc.monthly.month6 || 0),
            month7: Math.abs(rawActualAcc.monthly.month7 || 0),
            month8: Math.abs(rawActualAcc.monthly.month8 || 0),
            month9: Math.abs(rawActualAcc.monthly.month9 || 0),
            month10: Math.abs(rawActualAcc.monthly.month10 || 0),
            month11: Math.abs(rawActualAcc.monthly.month11 || 0),
            month12: Math.abs(rawActualAcc.monthly.month12 || 0),
          } as MonthlyTotals,
          ytd: Math.abs(rawActualAcc.ytd),
        } : null;

        return (
          <tr key={acc.code} className="bg-gray-50 border-b border-gray-100 hover:bg-gray-100">
            <td className="min-w-[180px] px-3 py-1.5 text-xs sticky left-0 bg-gray-50" style={{ paddingLeft: '44px' }}>
              <div className="flex items-center">
                <span className="font-mono text-gray-600 mr-2">{acc.code}</span>
                <span className="text-gray-500 truncate">{acc.description}</span>
              </div>
            </td>
            {MONTH_NAMES.map((_, i) => {
              const monthNum = i + 1;
              const showActual = comparisonMode && visibleActualMonths.has(monthNum);
              const projValue = getMonthValue(monthlyAcc, monthNum);
              const actualValue = actualAcc ? getMonthValue(actualAcc.monthly, monthNum) : 0;
              const varianceColor = actualAcc ? getVarianceColor(actualValue, projValue, false) : '';

              return (
                <React.Fragment key={i}>
                  <td className="min-w-[75px] px-1 py-1.5 text-right text-xs text-gray-600">
                    {formatCurrency(projValue)}
                  </td>
                  {showActual && (
                    <td className={`min-w-[70px] px-1 py-1.5 text-right text-xs bg-blue-50/50 ${varianceColor}`}>
                      {actualAcc && actualValue !== 0 ? formatCurrency(actualValue) : <span className="text-gray-300">--</span>}
                    </td>
                  )}
                </React.Fragment>
              );
            })}
            <td className="min-w-[100px] px-2 py-1.5 text-right text-xs font-medium bg-gray-50">
              {formatCurrency(accValue)}
              {perUnitLabel && <span className="text-gray-400 text-[10px] block">{perUnitLabel}</span>}
            </td>
            {comparisonMode && visibleActualMonths.size > 0 && (
              <td className={`min-w-[100px] px-2 py-1.5 text-right text-xs font-medium bg-blue-50/50 ${actualAcc ? getVarianceColor(actualAcc.ytd, accValue, false) : ''}`}>
                {actualAcc && actualAcc.ytd !== 0 ? formatCurrency(actualAcc.ytd) : <span className="text-gray-300">--</span>}
              </td>
            )}
          </tr>
        );
      })}
    </>
  );
};

/**
 * Props for the Department Table Header
 */
interface DepartmentTableHeaderProps {
  comparisonMode: boolean;
  visibleActualMonths: Set<number>;
  actualData: unknown;
  toggleActualMonth: (monthNum: number) => void;
  targetYear: number;
}

/**
 * Renders the table header row with month columns
 */
export const DepartmentTableHeader: React.FC<DepartmentTableHeaderProps> = ({
  comparisonMode,
  visibleActualMonths,
  actualData,
  toggleActualMonth,
  targetYear,
}) => {
  const hasActualForMonth = actualData !== null && actualData !== undefined;

  return (
    <thead className="sticky top-0 z-10">
      <tr className="bg-gray-700 text-white text-xs">
        <th className="min-w-[180px] px-3 py-2 text-left font-semibold sticky left-0 bg-gray-700 z-20">
          Metric
        </th>
        {MONTH_NAMES.map((month, i) => {
          const monthNum = i + 1;
          const showActual = comparisonMode && visibleActualMonths.has(monthNum);

          return (
            <React.Fragment key={month}>
              <th
                className={`min-w-[70px] px-1 py-2 text-right font-semibold ${comparisonMode && hasActualForMonth ? 'cursor-pointer hover:bg-gray-600' : ''}`}
                onClick={() => comparisonMode && hasActualForMonth && toggleActualMonth(monthNum)}
                title={comparisonMode && hasActualForMonth ? `Click to ${showActual ? 'hide' : 'show'} actuals` : ''}
              >
                <span className="flex items-center justify-end gap-1">
                  {month}
                  {comparisonMode && hasActualForMonth && (
                    <span className={`text-[9px] ${showActual ? 'text-blue-300' : 'text-gray-400'}`}>
                      {showActual ? '▼' : '▶'}
                    </span>
                  )}
                </span>
                {showActual && <span className="block text-xs text-gray-300 font-normal">Proj</span>}
              </th>
              {showActual && (
                <th className="min-w-[70px] px-1 py-2 text-right font-semibold bg-blue-600/50">
                  <span className="block text-xs text-blue-200 font-normal">Act</span>
                </th>
              )}
            </React.Fragment>
          );
        })}
        <th className="min-w-[100px] px-2 py-2 text-right font-bold bg-blue-600">
          {targetYear} Proj
        </th>
        {comparisonMode && visibleActualMonths.size > 0 && (
          <th className="min-w-[100px] px-2 py-2 text-right font-bold bg-blue-700">
            {targetYear} Act
          </th>
        )}
      </tr>
    </thead>
  );
};

/**
 * Props for the Department Header (collapsed view with summary)
 */
interface DepartmentHeaderProps {
  dept: DepartmentTemplate;
  isExpanded: boolean;
  toggleDepartment: (name: string) => void;
  projectedGross: number;
  projectedExpenses: number;
  projectedProfit: number;
  projectedUnits: number;
  hasProjections: boolean;
  comparisonMode: boolean;
  expandedCharts: Set<string>;
  toggleChart: (name: string) => void;
  formatCurrency: (value: number) => string;
  targetYear: number;
}

/**
 * Renders the collapsible department header with summary values
 */
export const DepartmentHeader: React.FC<DepartmentHeaderProps> = ({
  dept,
  isExpanded,
  toggleDepartment,
  projectedGross,
  projectedExpenses,
  projectedProfit,
  projectedUnits,
  hasProjections,
  comparisonMode,
  expandedCharts,
  toggleChart,
  formatCurrency,
  targetYear,
}) => {
  const projectedProfitColor = projectedProfit >= 0 ? 'text-green-600' : 'text-red-600';

  return (
    <div
      className="px-4 py-3 border-b border-gray-200 cursor-pointer hover:bg-gray-50 print-department-header"
      onClick={() => toggleDepartment(dept.name)}
    >
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-3">
          <span className="text-lg text-gray-400">
            {isExpanded ? '▼' : '▶'}
          </span>
          <h3 className="text-lg font-semibold text-gray-900">
            {dept.name === 'MISC' ? dept.displayName : `${dept.displayName} Department`}
            {(dept.name === 'NEW_VEHICLE' || dept.name === 'USED_VEHICLE') && projectedUnits > 0 && (
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({projectedUnits.toLocaleString()} units projected)
              </span>
            )}
          </h3>
        </div>
        <div className="flex items-center gap-3">
          {/* Chart toggle button (not for MISC) */}
          {comparisonMode && dept.name !== 'MISC' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleChart(dept.name);
              }}
              className={`p-1.5 rounded hover:bg-blue-100 transition-colors no-print ${expandedCharts.has(dept.name) ? 'bg-blue-100' : ''}`}
              title={expandedCharts.has(dept.name) ? 'Hide comparison chart' : 'Show comparison chart'}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4v16" />
              </svg>
            </button>
          )}
          <div className="text-right">
            {hasProjections ? (
              <>
                <div className={`text-lg font-bold ${dept.name === 'MISC' ? 'text-gray-700' : dept.name === 'OTHERS' ? 'text-green-600' : projectedProfitColor}`}>
                  {formatCurrency(
                    dept.name === 'MISC' ? projectedExpenses :
                    dept.name === 'OTHERS' ? projectedGross :
                    projectedProfit
                  )}
                  <span className="text-xs font-normal text-green-600 ml-1">({targetYear} Projection)</span>
                </div>
                {dept.name !== 'MISC' && dept.name !== 'OTHERS' && (
                  <div className="text-xs text-gray-500">
                    Gross: {formatCurrency(projectedGross)} | Exp: {formatCurrency(projectedExpenses)}
                  </div>
                )}
              </>
            ) : (
              <div className="text-sm text-gray-400 italic">
                No projections entered
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Props for the Department Comparison Chart section
 */
interface DepartmentComparisonChartSectionProps {
  dept: DepartmentTemplate;
  comparisonMode: boolean;
  expandedCharts: Set<string>;
  monthlyGross: MonthlyTotals;
  monthlyExpenses: MonthlyTotals;
  monthlyProfit: MonthlyTotals;
  projectedUnits: number;
  // Actual data getters
  getActualDeptGross: (deptName: string) => { monthly: MonthlyTotals; ytd: number } | null;
  getActualDeptExpenses: (deptName: string) => { monthly: MonthlyTotals; ytd: number } | null;
  getActualDeptProfit: (deptName: string) => { monthly: MonthlyTotals; ytd: number } | null;
  getDeptUnitSeasonalityWeights: (deptName: string) => number[];
  applySeasonality: (value: number, weights: number[]) => MonthlyTotals;
  actualUnitCounts: {
    monthlyNewUnits?: MonthlyTotals;
    monthlyUsedUnits?: MonthlyTotals;
  } | null;
}

/**
 * Renders the comparison chart for a department when in comparison mode
 */
export const DepartmentComparisonChartSection: React.FC<DepartmentComparisonChartSectionProps> = ({
  dept,
  comparisonMode,
  expandedCharts,
  monthlyGross,
  monthlyExpenses,
  monthlyProfit,
  projectedUnits,
  getActualDeptGross,
  getActualDeptExpenses,
  getActualDeptProfit,
  getDeptUnitSeasonalityWeights,
  applySeasonality,
  actualUnitCounts,
}) => {
  // Don't render if not in comparison mode or chart not expanded or MISC department
  if (!comparisonMode || !expandedCharts.has(dept.name) || dept.name === 'MISC') {
    return null;
  }

  const actualGrossData = getActualDeptGross(dept.name);
  const actualExpensesData = getActualDeptExpenses(dept.name);
  const actualProfitData = getActualDeptProfit(dept.name);

  // For vehicle departments, include units
  const showUnits = dept.hasFandI;
  let projectedUnitsMonthly: MonthlyTotals | undefined;
  let actualUnitsMonthly: MonthlyTotals | null | undefined;

  if (showUnits) {
    const deptUnitWeights = getDeptUnitSeasonalityWeights(dept.name);
    projectedUnitsMonthly = applySeasonality(projectedUnits, deptUnitWeights);
    actualUnitsMonthly = dept.name === 'NEW_VEHICLE'
      ? actualUnitCounts?.monthlyNewUnits
      : actualUnitCounts?.monthlyUsedUnits;
  }

  // For OTHERS, only show gross
  const grossOnly = dept.name === 'OTHERS';

  return (
    <ProjectionComparisonChart
      title={`${dept.displayName} - Projected vs Actual by Month`}
      projectedGross={monthlyGross}
      actualGross={actualGrossData?.monthly || null}
      projectedExpenses={monthlyExpenses}
      actualExpenses={actualExpensesData?.monthly || null}
      projectedProfit={monthlyProfit}
      actualProfit={actualProfitData?.monthly || null}
      projectedUnits={projectedUnitsMonthly}
      actualUnits={actualUnitsMonthly}
      showUnits={showUnits}
      grossOnly={grossOnly}
    />
  );
};
